/**
 * Mojo Up AI Cloud: the one way to Teams, from wherever the question comes up (JP, 22 Oct 2026: a person's own
 * space is an account, and a team works only on Teams). From a personal space it is "Start Teams": name the team,
 * choose seats, and the team is made, made active and sent to Stripe for its free trial (or Checkout, when the trial
 * was used) in one modal. From a free team its owners start the trial or upgrade directly; anyone else is told who
 * can. A write the API refuses with 402 `teams_required` anywhere in the console opens the same prompt
 * (TeamsRequiredPrompt, mounted once in the shell).
 */

import { CLOUD_TEAMS_PRICE_TEXT, CLOUD_TRIAL_DAYS, type CloudBilling } from '@aioe/shared';
import { Check } from '@gravity-ui/icons';
import { Button, Input, Label, TextField } from '@heroui/react';
import { useEffect, useState, type ReactNode } from 'react';

import { problemText, routes, TEAMS_REQUIRED_EVENT, type TeamsRequiredDetail } from '../../api/client.ts';
import { useCloud } from '../../auth/AuthProvider.tsx';
import { useCloudStanding } from '../../cloudPlan.ts';
import { useLoad } from '../../hooks.ts';
import { ListBlock, ListGroup } from '../ui.tsx';
import type { ArtTone } from '../ui/Art.tsx';
import { FormModal } from './FormModal.tsx';
import { Turnstile, useTurnstileSiteKey } from './Turnstile.tsx';

/** What Teams adds to a free account, in a person's words. */
export const TEAMS_INCLUDES = [
  'One sign-in for every workbench and node on the team',
  'Remote control of your workbenches from the console or your phone',
  'Shared projects, memory, catalogue and design systems',
  'Policies every workbench follows',
  'Portfolio and budgets for the people in the team',
  '2 GB of storage per person, pooled across the team',
] as const;

const MAX_SEATS = 500;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * The flow itself. Without `organisationId` (a personal space) it names and makes the team first; with one (a free
 * team the person owns) it goes straight to seats. Either way it ends at Stripe, or, where this deployment sells
 * nothing, on the new team.
 */
export function StartTeamsModal({ isOpen, onClose, organisationId, billing }: { isOpen: boolean; onClose: () => void; organisationId?: string; billing?: CloudBilling }) {
  const cloud = useCloud();
  const [name, setName] = useState('');
  const minimum = billing ? Math.max(1, billing.members + billing.openInvitations) : 1;
  const [seats, setSeats] = useState(String(Math.max(minimum, 2)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  // The team made in this modal: a retry after a refused Checkout must not make a second one.
  const [made, setMade] = useState<string>();
  const siteKey = useTurnstileSiteKey();
  const [check, setCheck] = useState<string>();
  const [checkRound, setCheckRound] = useState(0);
  const target = organisationId ?? made;
  const naming = !target;
  const n = Number(seats);
  const validSeats = Number.isInteger(n) && n >= minimum && n <= MAX_SEATS;
  const canSubmit = validSeats && (!naming || (name.trim().length > 0 && (!siteKey || Boolean(check))));
  const trial = billing ? billing.canStartTrial : true;

  /** Closing after the team was made lands on it, whatever came after. */
  const close = (id = made) => {
    if (id && !organisationId) cloud?.switchTo(id);
    onClose();
  };
  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      let id = target;
      if (!id) {
        const org = await routes.createOrganisation(name.trim(), check).finally(() => {
          setCheck(undefined);
          setCheckRound((r) => r + 1);
        });
        id = org.id;
        setMade(id);
        await cloud?.reload();
      }
      // Billing names the team in its path, so the new team need not be active yet: it becomes active when the
      // modal closes (here, or after a refusal), or on Stripe's return page, which switches to it.
      const b = billing && id === organisationId ? billing : await routes.billing(id);
      if (!b.configured) {
        // This deployment sells nothing: the team is made, and that is all there is to do.
        close(id);
        return;
      }
      window.location.assign((await routes.startCheckout(id, n)).url);
    } catch (e) {
      setError(problemText(e));
      setBusy(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={() => close()}
      title={naming ? 'Start Teams' : trial ? 'Start a free trial of Teams' : 'Upgrade to Teams'}
      blurb={
        trial
          ? `${CLOUD_TRIAL_DAYS} days free with no card, then ${CLOUD_TEAMS_PRICE_TEXT}. Stripe asks for your organisation's legal name and ABN on the next page.`
          : `${CLOUD_TEAMS_PRICE_TEXT}. Stripe asks for a card, your organisation's legal name and ABN on the next page.`
      }
      submitLabel={busy ? 'Working…' : 'Continue to Stripe'}
      canSubmit={canSubmit}
      busy={busy}
      error={error}
      onSubmit={() => void submit()}
    >
      {naming ? (
        <TextField variant="secondary" value={name} onChange={setName} isRequired autoFocus>
          <Label>Team name</Label>
          <Input placeholder="Acme engineering" maxLength={100} />
        </TextField>
      ) : made ? (
        <p className="text-sm text-muted">The team {name.trim() ? <span className="font-medium text-foreground">{name.trim()}</span> : null} is made and you are its owner; only the payment step is left.</p>
      ) : null}
      <TextField variant="secondary" value={seats} onChange={setSeats} isRequired autoFocus={!naming}>
        <Label>Seats</Label>
        <Input type="number" inputMode="numeric" min={minimum} max={MAX_SEATS} className="max-w-32 tabular-nums" />
      </TextField>
      <p className="text-xs text-muted">One seat per person{billing ? ' and per open invitation' : ', including you'}, at least {minimum}. Add more any time.</p>
      {naming && siteKey && isOpen ? <Turnstile key={checkRound} siteKey={siteKey} onToken={setCheck} /> : null}
    </FormModal>
  );
}

/** The team's owners, by name, for a person who cannot buy Teams themselves. */
function useOwners(organisationId: string | undefined): string | undefined {
  const members = useLoad(() => (organisationId ? routes.organisationMembers(organisationId).catch(() => undefined) : Promise.resolve(undefined)), [organisationId]);
  const owners = (members.data?.members ?? []).filter((m) => m.role === 'owner').map((m) => m.name ?? m.email ?? 'the owner');
  return owners.length ? owners.join(', ') : undefined;
}

/**
 * One button that opens the flow for the active organisation: "Start Teams" from a personal space, "Start free
 * trial" or "Upgrade to Teams" for a free team's owner. Renders nothing for anyone who cannot buy (a free team's
 * admins and members, a team already on Teams) or outside Cloud.
 */
export function UpgradeToTeamsButton({ size = 'sm', variant = 'primary', billing }: { size?: 'sm' | 'md'; variant?: 'primary' | 'secondary'; billing?: CloudBilling }) {
  const standing = useCloudStanding();
  const cloud = useCloud();
  const [open, setOpen] = useState(false);
  // Nothing to buy on a deployment that does not sell Teams.
  if (!standing.cloud || standing.teams || (!standing.personal && !standing.mayBuy) || billing?.configured === false) return null;
  const label = standing.personal ? 'Start Teams' : (billing?.canStartTrial ?? true) ? 'Start free trial' : 'Upgrade to Teams';
  return (
    <>
      <Button size={size} variant={variant} onPress={() => setOpen(true)}>
        {label}
      </Button>
      {open ? <StartTeamsModal isOpen onClose={() => setOpen(false)} {...(standing.personal ? {} : { organisationId: cloud!.organisation.id })} {...(billing ? { billing } : {})} /> : null}
    </>
  );
}

/**
 * The Teams card: what Teams adds, its price and trial, and the one action that fits the active organisation. For
 * Home, Billing and any view a free account cannot use. `reason` replaces the opening sentence (the
 * view's own words: "Shared projects come with Teams."); `actions` sit beside the button (at most two more).
 */
export function UpgradeToTeamsCard({ reason, billing, actions }: { tone?: ArtTone; reason?: ReactNode; billing?: CloudBilling; actions?: ReactNode }) {
  const standing = useCloudStanding();
  const cloud = useCloud();
  const owners = useOwners(standing.cloud && !standing.personal && !standing.mayBuy ? cloud?.organisation.id : undefined);
  if (!standing.cloud || standing.teams) return null;
  const trial = billing?.configured !== false && (billing?.canStartTrial ?? true);
  const opening =
    reason ??
    (standing.personal
      ? 'Work with your team in AI Workbench: one sign-in, shared projects and policies, and remote control from anywhere.'
      : `Start Teams to pick up where ${cloud?.organisation.name ?? 'this team'} left off. Everything it holds is still here.`);
  const who = billing?.configured === false ? 'Teams can’t be bought online right now. Ask us through Help and support.' : standing.personal || standing.mayBuy ? undefined : `Only an owner can start Teams. Ask ${owners ?? "the team's owner"}.`;
  return (
    <ListGroup
      title="Teams"
      description={`${CLOUD_TEAMS_PRICE_TEXT}.`}
      actions={
        <>
          <UpgradeToTeamsButton {...(billing ? { billing } : {})} />
          {actions}
        </>
      }
    >
      <ListBlock className="grid gap-3">
        <p className="m-0 text-sm text-muted">
          {opening} {trial ? `Free for ${CLOUD_TRIAL_DAYS} days, no card needed.` : ''}
        </p>
        <ul className="m-0 grid list-none gap-x-6 gap-y-1.5 p-0 text-sm @[34rem]:grid-cols-2">
          {TEAMS_INCLUDES.map((t) => (
            <li key={t} className="flex items-start gap-2">
              <Check className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden="true" />
              <span className="min-w-0">{t}</span>
            </li>
          ))}
        </ul>
        {who ? <p className="m-0 text-xs text-muted">{who}</p> : null}
      </ListBlock>
    </ListGroup>
  );
}

/**
 * Mounted once in the shell: when any request is refused 402 `teams_required`, the API's own sentence and the way to
 * Teams. One at a time, and the same sentence is not shown again for a minute after it was closed, so a view that
 * retries does not keep opening it.
 */
export function TeamsRequiredPrompt() {
  const standing = useCloudStanding();
  const cloud = useCloud();
  const [message, setMessage] = useState<string>();
  const [buying, setBuying] = useState(false);
  const [closed, setClosed] = useState<{ text: string; at: number }>();
  const owners = useOwners(message && !standing.personal && !standing.mayBuy ? cloud?.organisation.id : undefined);
  useEffect(() => {
    const on = (e: Event) => {
      const text = (e as CustomEvent<TeamsRequiredDetail>).detail.message;
      setMessage((open) => open ?? (closed && closed.text === text && Date.now() - closed.at < 60_000 ? undefined : text));
    };
    window.addEventListener(TEAMS_REQUIRED_EVENT, on);
    return () => window.removeEventListener(TEAMS_REQUIRED_EVENT, on);
  }, [closed]);
  if (!standing.cloud) return null;
  const close = () => {
    if (message) setClosed({ text: message, at: Date.now() });
    setMessage(undefined);
  };
  if (buying) return <StartTeamsModal isOpen onClose={() => setBuying(false)} {...(standing.personal ? {} : { organisationId: cloud!.organisation.id })} />;
  const mayBuy = standing.personal || standing.mayBuy;
  return (
    <FormModal
      isOpen={Boolean(message)}
      onClose={close}
      title="This needs Teams"
      blurb={message}
      submitLabel={standing.personal ? 'Start Teams' : 'Upgrade to Teams'}
      onSubmit={() => {
        close();
        setBuying(true);
      }}
      {...(mayBuy
        ? {}
        : {
            footer: (
              <Button variant="primary" onPress={close}>
                OK
              </Button>
            ),
          })}
    >
      <p className="text-sm text-muted">
        {mayBuy ? `Teams is ${CLOUD_TEAMS_PRICE_TEXT}. A team's first ${plural(CLOUD_TRIAL_DAYS, 'day')} are free, with no card.` : `${owners ?? "The team's owner"} can start Teams for the team.`}
      </p>
    </FormModal>
  );
}
